import { Checkmark } from '@travel/icons/ui';
import { GetTranslation } from '@travel/translation';
import { Facility } from '@travel/traveler-core/types/providerRatePlan';
import { isNotEmptyArray } from '@travel/utils';

import { EMPTY_ARRAY } from 'constants/defaultValue';
import { PLAN_FEATURES } from 'constants/planFeatures';
import { BedSizeItem, Capacity, Feature, RoomSizeItem } from 'ProviderRatePlan-Types';

type IconText = {
  id: string;
  icon?: string;
  name?: string;
  text?: string;
  // detail
  title?: string;
  description?: string;
};

export function getCapacityText(capacity: Capacity, getTranslation: GetTranslation) {
  const { minGuests, maxGuests } = capacity;
  if (!minGuests || !maxGuests) {
    return '';
  }

  return getTranslation({
    id: 'Common_Plan_Details.Room_Plan_Info.Room_Info.Capacity',
    data: {
      min_guests: minGuests,
      max_guests: maxGuests,
    },
  });
}

export function getMaxAdultChild(capacity: Capacity, getTranslation: GetTranslation) {
  const { maxAdults, maxChildren } = capacity;
  if (!maxAdults && !maxChildren) {
    return '';
  }

  const maxText = getTranslation({ id: 'Common_Plan_Details.Room_Plan_Info.Room_Info.Max' });
  const adultText =
    !!maxAdults &&
    getTranslation({
      id: 'Common_Plan_Details.Room_Plan_Info.Room_Info.Adult',
      count: maxAdults,
      data: { adult_guest_count: maxAdults },
    });
  const childText =
    !!maxChildren &&
    getTranslation({
      id: 'Common_Plan_Details.Room_Plan_Info.Room_Info.Child',
      count: maxChildren,
      data: { child_count: maxChildren },
    });

  return `${maxText} ${
    adultText && childText ? `${adultText} / ${childText}` : `${adultText ? adultText : childText}`
  } `;
}

export function getBedText(beds: Array<BedSizeItem>) {
  if (!isNotEmptyArray(beds)) {
    return '';
  }
  return beds?.map((bed: BedSizeItem) => `${bed.amount || 1} x ${bed.name}`).join(', ');
}

export function getRoomSizeText(room?: RoomSizeItem) {
  return room?.area ? `${room.area}${room.unit}` : null;
}

export function mappedIconTextListData(
  facilities: Array<IconText>,
  iconClassName?: string,
  iconPlaceHolder: React.ReactNode = <Checkmark size={16} />,
) {
  if (!isNotEmptyArray(facilities)) {
    return EMPTY_ARRAY;
  }

  // to remove duplicated feature id
  const features = Object.values(
    (facilities || EMPTY_ARRAY).reduce(
      (result, facility) => ({ ...result, [facility.id]: facility }),
      {},
    ),
  );

  return isNotEmptyArray(features)
    ? features.reduce((result, { name, text, icon, ...rest }) => {
        const label = name || text; // TODO: Remove `item.text` when all API are using `name` as feature title
        if (!label || label.trim().length === 0) {
          return result;
        }

        return [
          ...result,
          {
            text: label,
            icon: icon ? (
              <img src={icon} alt={name || text} className={iconClassName} loading={'lazy'} />
            ) : (
              iconPlaceHolder || null
            ),
            ...rest,
            groupId: undefined,
          },
        ];
      }, [])
    : EMPTY_ARRAY;
}

export function getFilteredRoomPlanFeatures(
  facilities?: Array<Facility>,
  start?: number,
  end?: number,
) {
  return mappedIconTextListData(
    isNotEmptyArray(facilities)
      ? facilities
          .reduce(
            (featureList, currentFacility) => [...featureList, ...currentFacility.features],
            [] as Array<Feature>,
          )
          .filter(
            feature =>
              feature.id !== PLAN_FEATURES.NO_MEAL && feature.id !== PLAN_FEATURES.CREDIT_CARD_ONLY, // To avoid duplication with paymentRestrictionFeature & hasMeal
          )
          .slice(start, end)
      : EMPTY_ARRAY,
  );
}
